/**
 * Utility functions for badge level calculations
 * These functions help determine badge levels, progress, and requirements based on sticker counts
 */

import images from '../assets/images/index'

// Types
interface BadgeLevel {
  title: React.ReactNode
  description: string
  icon: React.ReactNode
  minStickers: number
  maxStickers: number
  centerIcon: React.ReactNode
}

interface BadgeItem {
  title: React.ReactNode
  description: string
  icon: React.ReactNode
  centerIcon?: React.ReactNode
}

// Types for better type safety
export interface BadgeStats {
  earnedToday: number
  earnedThisWeek: number
  stickersToNext: number
  progressPercentage: number
}

export interface ApiError {
  message: string
  code?: string | number
}

// Badge items configuration - single source of truth
export const BADGE_ITEMS: BadgeItem[] = [
  {
    title: 'Rookie',
    description: '0-249',
    icon: <images.RookieIcon />,
    centerIcon: <images.RookieLarge />
  },
  {
    title: 'Student',
    description: '250-499',
    icon: <images.StudentBadge />,
    centerIcon: <images.StudentBadgeLarge />
  },
  {
    title: 'Scholar',
    description: '500-999',
    icon: <images.ScholarBadge />,
    centerIcon: <images.MathematicianBadge />
  },
  {
    title: 'Whiz',
    description: '1000-1499',
    icon: <images.WhizBadge />,
    centerIcon: <images.LightBadge />
  },
  {
    title: 'Ace',
    description: '1500-1999',
    icon: <images.AceBadge />,
    centerIcon: <images.AceBadgeLarge />
  },
  {
    title: 'Expert',
    description: '2000-2999',
    icon: <images.ExpertBadge />,
    centerIcon: <images.ExpertBadgeLarge />
  },
  {
    title: 'Mathematician',
    description: '3,000-3,999',
    icon: <images.MathematicianBadge />,
    centerIcon: <images.AwardBadge />
  },
  {
    title: 'Genius',
    description: '4,000-5,499',
    icon: <images.GeniusBadge />,
    centerIcon: <images.MicroScope />
  },
  {
    title: 'Mastermind',
    description: '5,500-7,499',
    icon: <images.MastermindBadge />,
    centerIcon: <images.TelescopeBadge />
  },
  {
    title: 'Champion',
    description: '7,500-9,999',
    icon: <images.ChampionBadge />,
    centerIcon: <images.TrophyLarge />
  },
  {
    title: 'Grand Champion',
    description: '10,000',
    icon: <images.TrophySmall />,
    centerIcon: <images.TrophyLarge />
  }
] as const

/**
 * Parses the description string to extract the range of stickers
 * Handles formats like: "0-249", "250-499", "3,000-3,999"
 * @param description - The description string containing the range
 * @returns Object with min and max values, or null if invalid
 */
export const parseDescriptionRange = (
  description: string
): { min: number; max: number } | null => {
  // Remove any extra whitespace and extract the range pattern
  const rangeMatch = description.match(
    /(\d{1,3}(?:,\d{3})*)-(\d{1,3}(?:,\d{3})*)/
  )

  if (!rangeMatch) {
    return null
  }

  // Extract min and max from regex groups
  const MIN_GROUP_INDEX = 1
  const MAX_GROUP_INDEX = 2
  const DECIMAL_BASE = 10

  // Remove commas and convert to numbers
  const min = parseInt(
    rangeMatch[MIN_GROUP_INDEX].replace(/,/g, ''),
    DECIMAL_BASE
  )
  const max = parseInt(
    rangeMatch[MAX_GROUP_INDEX].replace(/,/g, ''),
    DECIMAL_BASE
  )

  if (isNaN(min) || isNaN(max)) {
    return null
  }

  return { min, max }
}

/**
 * Gets the current badge level based on sticker count
 * @param stickerCount - The number of stickers the player has
 * @returns Current badge level object or null if not found
 */
const GRAND_CHAMPION_INDEX = 9

export const getCurrentBadgeLevel = (
  stickerCount: number
): BadgeLevel | null => {
  const matchingItem = BADGE_ITEMS.find((item) => {
    if (typeof item.description !== 'string') return false

    const range = parseDescriptionRange(item.description)
    if (!range) return BADGE_ITEMS[GRAND_CHAMPION_INDEX] // Default to Grand Champion if no range
    return stickerCount >= range.min && stickerCount <= range.max
  })

  if (matchingItem === undefined) return null

  const range = parseDescriptionRange(matchingItem.description)

  return {
    title: matchingItem.title,
    description: matchingItem.description,
    centerIcon: matchingItem.centerIcon,
    icon: matchingItem.icon,
    minStickers: range?.min ?? 0,
    maxStickers: range?.max ?? 0
  }
}

/**
 * Gets the next badge level the player can achieve
 * @param stickerCount - The number of stickers the player has
 * @returns Next badge level object or null if already at max
 */
export const getNextBadgeLevel = (stickerCount: number): BadgeLevel | null => {
  const nextLevel = BADGE_ITEMS.map((item) => {
    if (typeof item.description !== 'string') return null

    const range = parseDescriptionRange(item.description)
    if (!range || range.min <= stickerCount) return null

    return {
      title: item.title,
      description: item.description,
      icon: item.icon,
      minStickers: range.min,
      maxStickers: range.max
    }
  })
    .filter((level): level is BadgeLevel => level !== null)
    .reduce<BadgeLevel | null>((closest, current) => {
      if (!closest) return current
      return current.minStickers < closest.minStickers ? current : closest
    }, null)

  return nextLevel
}

/**
 * Calculates stickers needed to reach the next level
 * @param stickerCount - The number of stickers the player has
 * @returns Number of stickers needed for next level, or 0 if already at max
 */
export const getStickersToNextLevel = (stickerCount: number): number => {
  const nextLevel = getNextBadgeLevel(stickerCount)
  if (!nextLevel) {
    return 0 // Already at max level
  }

  return Math.max(0, nextLevel.minStickers - stickerCount)
}

/**
 * Calculates the progress percentage towards the next badge level
 * @param stickerCount - The number of stickers the player has
 * @returns Progress percentage (0-100)
 */
export const getBadgeProgressPercentage = (stickerCount: number): number => {
  const MAX_PERCENTAGE = 100
  const MIN_PERCENTAGE = 0

  const currentLevel = getCurrentBadgeLevel(stickerCount)
  const nextLevel = getNextBadgeLevel(stickerCount)

  if (!currentLevel || !nextLevel) {
    return MAX_PERCENTAGE // At max level
  }

  const currentLevelMin = currentLevel.minStickers
  const nextLevelMin = nextLevel.minStickers
  const progressInCurrentLevel = stickerCount - currentLevelMin
  const totalLevelRange = nextLevelMin - currentLevelMin

  return Math.min(
    MAX_PERCENTAGE,
    Math.max(
      MIN_PERCENTAGE,
      (progressInCurrentLevel / totalLevelRange) * MAX_PERCENTAGE
    )
  )
} /**
 * Gets the current step index for Steps component based on current badge level
 * @param currentBadgeLevel - The current badge level object
 * @returns Index of current badge in the array (0-based)
 */
export const getCurrentStepIndex = (
  currentBadgeLevel: BadgeLevel | null
): number => {
  if (!currentBadgeLevel) {
    return 0
  }

  // Find the index of current badge level in BADGE_ITEMS
  const currentIndex = BADGE_ITEMS.findIndex((item) => {
    if (
      typeof item.description === 'string' &&
      typeof currentBadgeLevel.description === 'string'
    ) {
      return item.description === currentBadgeLevel.description
    }
    return false
  })

  return currentIndex >= 0 ? currentIndex : 0
}

// Export types for use in other files
export type { BadgeLevel, BadgeItem }
